import {
  doc,
  increment,
  runTransaction, // Para recordPostLike
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { COLLECTIONS, db } from "./firebase";

// --- Funciones de Seguimiento de Vistas (con localStorage) ---

const VIEWED_POSTS_KEY = "viewedPosts";
const VIEW_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 horas

/**
 * Obtiene los posts vistos almacenados en localStorage.
 * @returns {Object} Objeto con postId como clave y timestamp de la vista como valor.
 */
const getViewedPosts = () => {
  try {
    const viewed = localStorage.getItem(VIEWED_POSTS_KEY);
    return viewed ? JSON.parse(viewed) : {};
  } catch (error) {
    console.error("Error reading viewed posts from localStorage:", error);
    return {};
  }
};

/**
 * Marca un post como visto en localStorage con un timestamp.
 * @param {string} postId ID del post a marcar.
 */
const markPostAsViewed = (postId) => {
  try {
    const viewed = getViewedPosts();
    viewed[postId] = Date.now();
    localStorage.setItem(VIEWED_POSTS_KEY, JSON.stringify(viewed));
  } catch (error) {
    console.error("Error saving viewed post to localStorage:", error);
  }
};

/**
 * Verifica si un post ha sido visto recientemente (según VIEW_EXPIRATION_MS).
 * @param {string} postId ID del post a verificar.
 * @returns {boolean} True si ha sido visto recientemente, false en caso contrario.
 */
export const hasViewedPost = (postId) => {
  const viewed = getViewedPosts();
  const lastViewedTimestamp = viewed[postId];
  if (!lastViewedTimestamp) {
    return false; // Nunca visto
  }
  // Verificar si la vista ha expirado
  return Date.now() - lastViewedTimestamp < VIEW_EXPIRATION_MS;
};

/**
 * Registra una vista para un post si no ha sido visto recientemente.
 * Incrementa el contador 'readCount' en Firestore.
 *
 * @param {string} postId ID del post.
 * @param {number} currentCount El contador actual de vistas (opcional, para UI optimista).
 * @returns {Promise<number>} El nuevo contador de vistas (o el actual si no se incrementó).
 */
export const recordPostView = async (postId, currentCount = 0) => {
  if (!postId) return currentCount;

  if (hasViewedPost(postId)) {
    console.log(
      `Post ${postId} ya visto recientemente. No se registra nueva vista.`,
    );
    return currentCount; // No registrar si ya fue visto recientemente
  }

  const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
  try {
    // Incrementar en Firestore
    await updateDoc(postRef, { readCount: increment(1) });
    // Marcar como visto en localStorage
    markPostAsViewed(postId);
    console.log(`Vista registrada para Post ${postId}`);
    return currentCount + 1; // Devolver valor incrementado para UI optimista
  } catch (error) {
    console.error(`Error registrando vista para Post ${postId}:`, error);
    // Si falla la actualización, no marcar como visto y devolver contador actual
    return currentCount;
  }
};

// --- Función de Seguimiento de Compartidos ---

/**
 * Registra que un post ha sido compartido en una plataforma específica.
 * Incrementa 'shareTotal' y el contador específico de la plataforma en Firestore.
 *
 * @param {string} postId ID del post.
 * @param {string} platform Nombre de la plataforma (ej: 'Facebook', 'Twitter', 'CopyLink').
 * @returns {Promise<boolean>} True si la actualización fue exitosa, false si hubo error.
 */
export const recordPostShare = async (postId, platform) => {
  if (!postId || !platform) return false;

  const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
  const platformKey = `share${
    platform.charAt(0).toUpperCase() + platform.slice(1)
  }`; // ej: shareFacebook

  try {
    const updateData = {
      shareTotal: increment(1),
      [platformKey]: increment(1), // Incremento dinámico para la plataforma
    };
    await updateDoc(postRef, updateData);
    console.log(`Compartido registrado para Post ${postId} en ${platform}`);
    return true;
  } catch (error) {
    console.error(
      `Error registrando compartido para Post ${postId} en ${platform}:`,
      error,
    );
    return false;
  }
};

// --- Función de Seguimiento de Likes (Versión Robusta con Transacción) ---

/**
 * Registra o deshace un "Me gusta" para un post por un usuario específico.
 * Utiliza una transacción de Firestore para garantizar la atomicidad y consistencia.
 * Previene likes duplicados y permite deshacer el like.
 *
 * @param {string} postId ID del post al que se da/quita like.
 * @param {string} userId ID del usuario autenticado que realiza la acción.
 * @returns {Promise<{success: boolean, newLikes: number, liked: boolean, message?: string}>}
 *          Un objeto indicando:
 *          - success: Si la operación fue exitosa.
 *          - newLikes: El nuevo contador total de likes del post.
 *          - liked: Booleano indicando si el usuario (`userId`) ahora tiene marcado el post como "Me gusta".
 *          - message: Mensaje de error opcional si success es false.
 */
export const recordPostLike = async (postId, userId) => {
  // 1. Validación rigurosa de entradas
  if (!postId || typeof postId !== "string" || postId.trim() === "") {
    console.error("recordPostLike: postId inválido o faltante.");
    return {
      success: false,
      newLikes: 0,
      liked: false,
      message: "ID de publicación no válido.",
    };
  }
  if (!userId || typeof userId !== "string" || userId.trim() === "") {
    console.error("recordPostLike: userId inválido o faltante.");
    return {
      success: false,
      newLikes: 0,
      liked: false,
      message: "Se requiere iniciar sesión para dar Me gusta.",
    };
  }

  // 2. Referencias
  const postRef = doc(db, COLLECTIONS.BLOG_POSTS, postId);
  const likeRef = doc(db, COLLECTIONS.BLOG_POSTS, postId, "likes", userId); // Subcolección 'likes'

  try {
    // 3. Ejecutar la lógica dentro de una transacción atómica
    const result = await runTransaction(db, async (transaction) => {
      // Leer DENTRO de la transacción
      const postDoc = await transaction.get(postRef);
      const likeDoc = await transaction.get(likeRef);

      if (!postDoc.exists()) {
        throw new Error("La publicación no existe o fue eliminada.");
      }

      const currentLikes = postDoc.data()?.likes || 0;
      let finalLikes;
      let userNowLikes;

      // 4. Lógica condicional: Like o Unlike
      if (likeDoc.exists()) {
        // --- Deshacer Like ---
        console.log(`Deshaciendo like: User ${userId} en Post ${postId}`);
        transaction.update(postRef, { likes: increment(-1) });
        transaction.delete(likeRef);
        finalLikes = Math.max(0, currentLikes - 1);
        userNowLikes = false;
      } else {
        // --- Registrar Like ---
        console.log(`Registrando like: User ${userId} en Post ${postId}`);
        transaction.update(postRef, { likes: increment(1) });
        transaction.set(likeRef, {
          likedAt: serverTimestamp(),
          userId,
          postId,
        });
        finalLikes = currentLikes + 1;
        userNowLikes = true;
      }
      return { newLikes: finalLikes, liked: userNowLikes };
    });

    // 5. Éxito de la transacción
    console.log(
      `Like/Unlike procesado con éxito para Post ${postId}. Nuevo estado:`,
      result,
    );
    return { success: true, ...result };
  } catch (error) {
    // 6. Manejo de errores (fallo transacción o error lanzado)
    console.error(
      `Error crítico en transacción de like/unlike para Post ${postId} por User ${userId}:`,
      error,
    );
    return {
      success: false,
      newLikes: 0, // No podemos garantizar el contador
      liked: false, // No podemos garantizar el estado
      message: `Error al procesar la acción: ${
        error.message || "Error desconocido."
      }`,
    };
  }
};

// --- Exportación del Servicio ---

// Objeto que agrupa las funciones del servicio
const analyticsService = {
  recordPostView,
  hasViewedPost, // Exportar también la función de verificación si se usa externamente
  recordPostShare,
  recordPostLike,
};

// Exportación por defecto del objeto del servicio
export default analyticsService;
